import {
  confirmSignInWithNewPassword,
  signInWithPassword,
} from "@src/appV2/Auth/cognito/cognitoPasswordAuth";
import { signInWithPasswordPreservingEmailOtp } from "@src/appV2/Auth/cognito/passwordLoginWithPendingEmailOtp";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import {
  recordLoginAttemptInitiated,
  recordTerminalLoginFailure,
} from "@src/appV2/Auth/loginJourneyTelemetry";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  logPasswordLoginFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";
import { APP_V2_APP_EVENTS, APP_V3_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";

interface LoginWithEmailAndPasswordParams {
  email: string;
  password: string;
}

interface LoginWithEmailAndPasswordPreservingEmailOtpParams extends LoginWithEmailAndPasswordParams {
  onBeforeAmplifySignIn: () => void;
}

interface RunPasswordLoginAttemptParams extends LoginWithEmailAndPasswordParams {
  authenticate: (params: LoginWithEmailAndPasswordParams) => Promise<unknown>;
}

/**
 * Outcome of a password sign-in attempt. `requiresNewPassword` is the
 * forced-change branch: the user signed in with a temporary password and must
 * set a new one (via `completeNewPasswordChallenge`) before sign-in completes.
 */
export type LoginWithEmailAndPasswordResult =
  | { success: true }
  | { success: false; requiresNewPassword: true }
  | { success: false; requiresNewPassword?: false; error: string; errorCode: string };

/**
 * Handles Cognito email/password authentication for mobile.
 */
export async function loginWithEmailAndPassword({
  email,
  password,
}: LoginWithEmailAndPasswordParams): Promise<LoginWithEmailAndPasswordResult> {
  return await runPasswordLoginAttempt({
    email,
    password,
    authenticate: authenticateWithAmplify,
  });
}

/**
 * Runs one logical password attempt while an Amplify EMAIL_OTP challenge is
 * pending. The side-channel probe and the successful Amplify replay share the
 * same initiation/terminal telemetry emitted by `runPasswordLoginAttempt`.
 */
export async function loginWithEmailAndPasswordPreservingEmailOtp({
  email,
  password,
  onBeforeAmplifySignIn,
}: LoginWithEmailAndPasswordPreservingEmailOtpParams): Promise<LoginWithEmailAndPasswordResult> {
  return await runPasswordLoginAttempt({
    email,
    password,
    authenticate: async (params) => {
      await signInWithPasswordPreservingEmailOtp({
        ...params,
        onBeforeAmplifySignIn,
      });
    },
  });
}

/**
 * Completes the forced password change after `loginWithEmailAndPassword`
 * returned `requiresNewPassword`. Submits the chosen password to the pending
 * Cognito challenge; on success the Cognito session is established and the
 * caller can hydrate the app session. Password-policy violations surface as
 * `auth/invalid-password` with an inline-friendly message.
 */
export async function completeNewPasswordChallenge({
  newPassword,
}: {
  newPassword: string;
}): Promise<{ success: true } | { success: false; error: string }> {
  try {
    await confirmSignInWithNewPassword(newPassword);

    logEvent(APP_V3_APP_EVENTS.COGNITO_FORCE_CHANGE_PASSWORD_SUCCESS, {
      outcome: LOGIN_OUTCOME.SUCCESS,
    });
    // Journey completion happens in the caller after hydration (see above).

    return { success: true };
  } catch (error: unknown) {
    // The NEW_PASSWORD_REQUIRED challenge lives in the in-memory Amplify session
    // from the original sign-in; once it expires, confirmSignIn throws
    // NotAuthorizedException. Routing that through mapCognitoError("password")
    // would mis-label it auth/user-not-found / auth/expired-code (both
    // misleading here), so surface a challenge-specific "sign in again" message.
    const mapped = isChallengeSessionExpired(error)
      ? {
          code: "auth/challenge-session-expired",
          message:
            "Your session expired before your new password could be set. Please sign in again to continue.",
        }
      : mapCognitoError(error, "password");
    const { outcome, reason } =
      mapped.code === "auth/unknown" && isNetworkFailure(error)
        ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
        : classifyCognitoError(mapped.code);

    recordTerminalLoginFailure();
    logPasswordLoginFailure({
      eventName: APP_V3_APP_EVENTS.COGNITO_FORCE_CHANGE_PASSWORD_FAILURE,
      error,
      metadata: { errorCode: mapped.code, outcome, reason },
    });

    return { success: false, error: mapped.message };
  }
}

/**
 * A NotAuthorizedException from `confirmSignIn` on the NEW_PASSWORD_REQUIRED
 * challenge means the in-memory sign-in session backing the challenge is gone or
 * expired — not a wrong-credentials/missing-user case (a bad new password throws
 * InvalidPasswordException instead).
 */
function isChallengeSessionExpired(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: string }).name === "NotAuthorizedException"
  );
}

async function runPasswordLoginAttempt({
  email,
  password,
  authenticate,
}: RunPasswordLoginAttemptParams): Promise<LoginWithEmailAndPasswordResult> {
  setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_PASSWORD);
  recordLoginAttemptInitiated({ method: IDENTITY_PROVIDER.COGNITO_PASSWORD, email });

  try {
    // Support-generated temporary passwords are commonly pasted from Slack/email
    // with a leading/trailing space, which Cognito's ^[\S]+.*[\S]+$ validation
    // rejects as a wrong credential. Trim it — a valid password never carries
    // edge whitespace, so this only strips the paste artifact. Mirrors the
    // Change Password dialog; the forced-change *new* password is left as typed.
    await authenticate({ email, password: password.trim() });

    logEvent(APP_V2_APP_EVENTS.PASSWORD_LOGIN_COGNITO_SESSION_SUCCESS, {
      outcome: LOGIN_OUTCOME.SUCCESS,
    });
    // The login journey is completed by the caller after session hydration
    // succeeds (see Login.tsx), not here — Cognito auth alone isn't a terminal
    // login success (a worker account or hydration failure still fails).

    return { success: true };
  } catch (error: unknown) {
    const mapped = mapCognitoError(error, "password");

    // Forced password change isn't a failure — the temporary password was
    // accepted and Cognito is asking for a replacement. Surface it as a distinct
    // branch so the caller can show the set-a-new-password screen.
    if (mapped.code === "auth/new-password-required") {
      return { success: false, requiresNewPassword: true };
    }

    const { outcome, reason } =
      mapped.code === "auth/unknown" && isNetworkFailure(error)
        ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
        : classifyCognitoError(mapped.code);

    recordTerminalLoginFailure();
    logPasswordLoginFailure({
      eventName: APP_V2_APP_EVENTS.PASSWORD_LOGIN_COGNITO_SESSION_FAILURE,
      error,
      metadata: { errorCode: mapped.code, outcome, reason },
    });

    return { success: false, error: mapped.message, errorCode: mapped.code };
  }
}

async function authenticateWithAmplify({
  email,
  password,
}: LoginWithEmailAndPasswordParams): Promise<void> {
  await signInWithPassword(email, password);
}
